import clsx from 'clsx'
import { Fragment, ReactNode } from 'react'
import { CellProps, Renderer } from 'react-table'
import { initialQueryState, QueryState } from '../../../_metronic/helpers'
import styles from './styles/DataTable.module.css'
import { TableColumn, TableColumnOptions, UseQueryRequestHook } from './tableTypes'

export type SortField = {
  /** Campo enviado no `sort` da API. */
  field: string
  label: string
  /**
   * `false` mostra só o rótulo, sem botão de ordenação — para a linha de apoio que a API não
   * ordena (ex.: "Nome / Descrição"). Padrão: ordenável.
   */
  sortable?: boolean
}

type SortDirection = 'ascending' | 'descending' | 'none'

/** Direção atual de um campo, considerando também os `fixedSort` do estado. */
export const getSortDirection = (state: QueryState, field: string): SortDirection => {
  if (state.sort === field) return state.order === 'desc' ? 'descending' : 'ascending'
  const fixed = state.fixedSort?.find((f) => f.field === field)
  if (fixed) return fixed.order === 'desc' ? 'descending' : 'ascending'
  return 'none'
}

/** aria-sort de uma coluna que pode ordenar por mais de um campo (ex.: nome / razão social). */
export const getColumnAriaSort = (state: QueryState, fields: string[] = []): SortDirection => {
  for (const field of fields) {
    const dir = getSortDirection(state, field)
    if (dir !== 'none') return dir
  }
  return 'none'
}

const SortIcon = ({ direction }: { direction: SortDirection }) => {
  const path =
    direction === 'ascending'
      ? 'm18 15-6-6-6 6'
      : direction === 'descending'
      ? 'm6 9 6 6 6-6'
      : 'm7 15 5 5 5-5M7 9l5-5 5 5'
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth={direction === 'none' ? 2 : 2.2}
      aria-hidden='true'
    >
      <path d={path} />
    </svg>
  )
}

type SortableHeaderProps = {
  fields: SortField[]
  useQueryRequest: UseQueryRequestHook
}

/**
 * Cabeçalho com um ou mais botões de ordenação (operáveis por teclado).
 * Com dois campos, renderiza "Nome fantasia / Razão social" — cada um ordena separadamente.
 */
export const SortableHeader = ({ fields, useQueryRequest }: SortableHeaderProps) => {
  const { state, updateState } = useQueryRequest()

  const toggle = (field: string) => {
    const current = getSortDirection(state, field)
    updateState({
      ...initialQueryState,
      sort: field,
      order: current === 'ascending' ? 'desc' : 'asc',
    })
  }

  return (
    <span className={styles.sortGroup}>
      {fields.map(({ field, label, sortable = true }, index) => {
        const direction = getSortDirection(state, field)
        return (
          <Fragment key={field}>
            {index > 0 && <span aria-hidden='true'>/</span>}
            {!sortable ? (
              <span>{label}</span>
            ) : (
              <button
                type='button'
                className={clsx(styles.sortButton, direction !== 'none' && styles.sortButtonActive)}
                onClick={() => toggle(field)}
              >
                {label}
                <SortIcon direction={direction} />
              </button>
            )}
          </Fragment>
        )
      })}
    </span>
  )
}

type SortableColumnConfig<T extends object> = TableColumnOptions & {
  id: string
  fields: SortField[]
  useQueryRequest: UseQueryRequestHook
  Cell: Renderer<CellProps<T>>
}

/**
 * Atalho para declarar uma coluna ordenável: monta o Header e o `sortFields` (aria-sort)
 * a partir da mesma lista de campos.
 *
 * @example
 * sortableColumn<EmpresaList>({
 *   id: 'nomeFantasia',
 *   fields: [{ field: 'nomeFantasia', label: 'Nome fantasia' }],
 *   useQueryRequest,
 *   Cell: ({ row }) => <StackedCell primary={row.original.nomeFantasia} />,
 * })
 */
export const sortableColumn = <T extends object>({
  fields,
  useQueryRequest,
  ...column
}: SortableColumnConfig<T>): TableColumn<T> =>
  ({
    ...column,
    sortFields: fields.filter((f) => f.sortable !== false).map((f) => f.field),
    Header: () => <SortableHeader fields={fields} useQueryRequest={useQueryRequest} />,
  } as TableColumn<T>)

/** Cabeçalho simples (sem ordenação) — só texto, para manter o mesmo estilo. */
export const PlainHeader = ({ children }: { children: ReactNode }) => <span>{children}</span>
